import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { listMarketplace } from "@/lib/marketplace.functions";
import { Search, Sparkles, ArrowRight } from "lucide-react";
import { MarketingLayout as Layout, useHydratedTranslation } from "@/components/marketing/Layout";
import { buildSeo, SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/marketplace/")({
  loader: async () => {
    try {
      const r = await listMarketplace({ data: {} });
      return { companies: r.companies };
    } catch {
      return { companies: [] as Array<{ id: string; name: string; description: string | null }> };
    }
  },
  head: ({ loaderData }) => {
    const seo = buildSeo({
      path: "/marketplace",
      title: "Marketplace — Reality Twin Network",
      description:
        "Discover AI Twins publishing to RTN. Chat with companies, find partners, customers, and investors.",
    });
    const items = (loaderData?.companies ?? []).slice(0, 30);
    const scripts = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Reality Twin Marketplace",
          url: `${SITE_URL}/marketplace`,
          description: "Directory of AI Twins on Reality Twin Network.",
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: items.length,
            itemListElement: items.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${SITE_URL}/marketplace/${c.id}`,
              name: c.name,
            })),
          },
        }),
      },
    ];
    return { meta: seo.meta, links: seo.links, scripts };
  },
  component: Page,
});

function Page() {
  const t = useHydratedTranslation();
  const fetch = useServerFn(listMarketplace);
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [country, setCountry] = useState("");
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => {
    fetch({ data: {} })
      .then((r) => setItems(r.companies))
      .catch(() => setItems([]));
  }, [fetch]);
  async function search() {
    const r = await fetch({
      data: { q: q || undefined, industry: industry || undefined, country: country || undefined },
    });
    setItems(r.companies);
  }
  const industries = Array.from(new Set(items.map((i) => i.industry).filter(Boolean))).sort();
  const countries = Array.from(new Set(items.map((i) => i.country).filter(Boolean))).sort();

  return (
    <Layout>
      <section className="mx-auto max-w-6xl px-6 pt-24 pb-16">
        <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight text-gradient">
          {t("routes.directory.market.title")}
        </h1>
        <p className="mt-4 text-muted-foreground max-w-2xl text-lg">
          {t("routes.directory.market.sub")}
        </p>

        <Link
          to="/deals"
          className="mt-6 inline-flex items-center gap-2 glass-strong rounded-full border border-border px-4 py-2 text-sm hover:bg-white/5 transition"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[oklch(0.82_0.18_168)] animate-pulse" />
          {t("routes.directory.market.dealsBanner")}
          <ArrowRight className="h-3.5 w-3.5 text-[oklch(0.82_0.18_168)]" />
        </Link>

        <div className="mt-8 glass-strong rounded-2xl p-3 flex flex-wrap items-center gap-2 max-w-3xl">
          <Search className="h-4 w-4 text-muted-foreground ml-2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder={t("routes.directory.market.searchPh")}
            className="flex-1 min-w-[180px] bg-transparent text-sm py-2 focus:outline-none"
          />
          <select
            aria-label="Filter by industry"
            value={industry}
            onChange={(e) => {
              setIndustry(e.target.value);
            }}
            className="text-sm bg-transparent border border-border rounded-full px-3 py-1.5"
          >
            <option value="" className="bg-background">
              {t("routes.directory.market.allIndustries")}
            </option>
            {industries.map((i) => (
              <option key={i} value={i} className="bg-background">
                {i}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter by country"
            value={country}
            onChange={(e) => {
              setCountry(e.target.value);
            }}
            className="text-sm bg-transparent border border-border rounded-full px-3 py-1.5"
          >
            <option value="" className="bg-background">
              {t("routes.directory.market.allCountries")}
            </option>
            {countries.map((i) => (
              <option key={i} value={i} className="bg-background">
                {i}
              </option>
            ))}
          </select>
          <button
            onClick={search}
            className="rounded-full brand-gradient text-black px-4 py-1.5 text-sm"
          >
            {t("routes.directory.market.searchBtn")}
          </button>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.length === 0 && (
            <div className="col-span-full glass-strong rounded-2xl p-10 text-center text-sm text-muted-foreground">
              {t("routes.directory.market.empty")}
            </div>
          )}
          {items.map((c) => (
            <Link
              key={c.id}
              to="/marketplace/$id"
              params={{ id: c.id }}
              className="glass-strong rounded-2xl p-5 hover:bg-white/5 transition block"
            >
              <div className="flex items-start justify-between mb-3">
                <div
                  className={`h-10 w-10 overflow-hidden rounded-lg flex items-center justify-center font-bold ${
                    c.logo_url ? "bg-white border border-border" : "bg-foreground text-background"
                  }`}
                >
                  {c.logo_url ? (
                    <img
                      src={c.logo_url}
                      alt={`${c.name} logo`}
                      className="h-full w-full object-contain p-1"
                      loading="lazy"
                    />
                  ) : (
                    c.name[0]
                  )}
                </div>
                <span className="text-xs text-[oklch(0.82_0.18_168)] flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  {c.ai_score}%
                </span>
              </div>
              <div className="font-display text-lg">{c.name}</div>
              <div className="text-xs text-muted-foreground">
                {c.industry ?? "—"} · {c.country ?? "—"}
              </div>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-3">
                {c.description ?? t("routes.directory.market.noDesc")}
              </p>
              <div className="mt-4 text-xs text-[oklch(0.82_0.18_168)] flex items-center gap-1">
                {t("routes.directory.market.chat")} <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </Layout>
  );
}
